import { Component, ChangeDetectionStrategy, ViewChild, computed, effect, DestroyRef, inject } from '@angular/core';
import { IHeaderParams } from 'ag-grid-community';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { tap } from 'rxjs/operators';
import { IrisDmsGlobalSandbox } from '@iris/common/modules/dms/sandbox/dms-global.sandbox';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { isNil } from 'lodash';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StrakitCheckboxComponent } from '@ngx-strakit/cdk/controls';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'dms-grid-checkbox-header-template',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,

    MatMenuModule,
    MatButtonModule,

    StrakitCheckboxComponent,
  ],
  templateUrl: './dms-grid-checkbox-header-template.component.html',
  styleUrls: ['./dms-grid-checkbox-header-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsGridCheckboxHeaderTemplateComponent implements IHeaderAngularComp {
  private readonly dmsGlobalSandbox = inject(IrisDmsGlobalSandbox);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(MatMenuTrigger) selectionMenu: MatMenuTrigger;

  readonly valueControl = new FormControl(false);

  private readonly selectedItemsCount = this.dmsFilesSandbox.selectedItemsCount;
  private readonly contentPageCount = this.dmsFilesSandbox.contentPageCount;
  private readonly hasAnySelection = this.dmsFilesSandbox.hasAnySelection;

  readonly selected = computed(() => this.selectedItemsCount() > 0 && this.selectedItemsCount() === this.contentPageCount());
  readonly indeterminate = computed(() => this.selectedItemsCount() > 0 && this.selectedItemsCount() < this.contentPageCount());

  private params: IHeaderParams;

  readonly controlEffect = effect(() => {
    this.valueControl.patchValue(this.selected(), { emitEvent: false });
  });

  agInit(params: IHeaderParams): void {
    this.params = params;

    this.valueControl.valueChanges.pipe(
      tap(() => this.checkboxClicked()),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  refresh(params: IHeaderParams): boolean {
    this.params = params;
    return true;
  }

  checkboxClicked(): void {
    this.valueControl.patchValue(false, { emitEvent: false });
    if (!this.hasAnySelection()) {
      this.selectionMenu.openMenu();
      return;
    }
    this.deselectAll();
  }

  selectCurrentPage(): void {
    const api = this.params.api;
    const ids = [];
    api.forEachNode((node) => {
      ids.push(node.data.id);
    });
    this.dmsFilesSandbox.toggleEntitiesSelection(ids, []);
  }

  selectAllPages(): void {
    this.dmsGlobalSandbox.setCheckAll(true);
    this.dmsFilesSandbox.resetSelection();
  }

  deselectAll(): void {
    this.dmsGlobalSandbox.setCheckAll(false);
    this.dmsFilesSandbox.resetSelection();
  }

  menuClosed(reason: string): void {
    if (isNil(reason)) {
      this.valueControl.setValue(false, { emitEvent: false });
    }
  }
}
